import { useMemo, useState } from "react"
import styled from "styled-components"

import {
  Banner,
  Button,
  Skeleton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { TEnhancedPropertyContext } from "src/components/AiAssistant/aiAssistantTypes"
import { SearchFilter } from "src/components/Filter/SearchFilter"
import { useFetchHomeGroups } from "src/data/homeGroups/queries/homeGroupQueries"
import { useFetchIntegrations } from "src/data/integrations/queries/integrationQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomes } from "src/data/organizations/queries/homeQueries"
import {
  useDeletePropertyContext,
  useFetchPropertyContexts,
} from "src/data/propertyContext/propertyContextQueries"
import { TPropertyContext } from "src/data/propertyContext/propertyContextTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { GridTable } from "src/ui/GridTable/GridTable"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { InternalLink } from "src/ui/Link/InternalLink"
import { unique } from "src/utils/genericUtil"
import { formatDate } from "src/utils/l10n"

export function AiAssistantContextLibrary({
  setShowCreateContextDialog,
}: {
  setShowCreateContextDialog: (show: boolean) => void
}) {
  const { t, tPlaceholder, langKeys } = useTranslate()

  const { navigate } = useRouter()

  const { org } = useOrganization()
  const user = useGetUser()

  const [search, setSearch] = useState("")

  const fetchPropertyContexts = useFetchPropertyContexts({
    orgId: org.id,
  })

  const deletePropertyContext = useDeletePropertyContext()

  function deleteContext(context: TPropertyContext) {
    deletePropertyContext.mutate({
      path: {
        organization_id: org.id,
        property_context_id: context.id,
      },
    })
  }

  const uniquePropertContextHomeIds = useMemo(() => {
    if (!fetchPropertyContexts.data) {
      return []
    }

    return unique(
      fetchPropertyContexts.data.property_contexts
        .map((context) => context.home_id)
        .filter((id) => !!id)
        // To make TS happy
        .filter((id) => id !== undefined)
    )
  }, [fetchPropertyContexts.data])

  const uniquePropertContextHomegroupIds = useMemo(() => {
    if (!fetchPropertyContexts.data) {
      return []
    }

    return unique(
      fetchPropertyContexts.data.property_contexts
        .map((context) => context.homegroup_id)
        .filter((id) => !!id)
        // To make TS happy
        .filter((id) => id !== undefined)
    )
  }, [fetchPropertyContexts.data])

  const fetchHomes = useFetchHomes({
    orgId: org.id,
    filters: {
      ids:
        uniquePropertContextHomeIds.length > 0
          ? uniquePropertContextHomeIds
          : undefined,
    },
  })

  const fetchHomegroups = useFetchHomeGroups({
    orgId: org.id,
    filter: {
      ids: uniquePropertContextHomegroupIds.join(",") || undefined,
    },
  })

  const fetchIntegrations = useFetchIntegrations({
    orgId: org.id,
    filters: {
      includeExternalIntegrations: true,
    },
  })

  const enhancedPropertyContexts = useMemo(() => {
    if (
      !fetchPropertyContexts.data ||
      !fetchHomes.data ||
      !fetchHomegroups.data ||
      !fetchIntegrations.data
    ) {
      return []
    }

    return fetchPropertyContexts.data.property_contexts.map((context) => {
      const home = fetchHomes.data.homes.find(
        (home) => home.home_id === context.home_id
      )

      const homegroup = fetchHomegroups.data.homegroups.find(
        (homegroup) => homegroup.id === context.homegroup_id
      )

      const integration = fetchIntegrations.data.integrations.find(
        (integration) => integration.integration_identifier === context.source
      )

      return {
        ...context,
        home: home,
        homegroup: homegroup,
        integration: integration,
      }
    })
  }, [
    fetchPropertyContexts.data,
    fetchHomes.data,
    fetchHomegroups.data,
    fetchIntegrations.data,
  ])

  const tableColumns: TableColumn<TEnhancedPropertyContext>[] = [
    {
      value: "title",
      label: tPlaceholder("Title"),
      renderCell: (data) => {
        if (data.source_url) {
          return (
            <div>
              <ExternalLink href={data.source_url}>{data.title}</ExternalLink>
            </div>
          )
        }

        return (
          <InternalLink
            to={Routes.AiAssistantKnowledgeContent.location({
              contextId: data.id,
            })}
          >
            {data.title}
          </InternalLink>
        )
      },
      columnWidth: "1fr",
    },

    {
      value: "applies_to",
      label: tPlaceholder("Applies to"),
      renderCell: (data) => (
        <Text>
          {data.home?.name ?? data.homegroup?.name ?? tPlaceholder("All units")}
        </Text>
      ),
      columnWidth: "1fr",
    },
    {
      value: "source",
      label: tPlaceholder("Source"),
      renderCell: (data) => {
        if (data.integration) {
          return (
            <div>
              <img
                src={data.integration.icon}
                alt={`${data.integration.name} logo`}
                style={{
                  width: "24px",
                  borderRadius: "4px",
                }}
              />
            </div>
          )
        }

        return <Text>-</Text>
      },
      columnWidth: "1fr",
    },
    {
      value: "updated_at",
      label: tPlaceholder("Updated at"),
      renderCell: (data) => (
        <Text>
          {formatDate({
            date: data.updated_at,
            clockType: user.clock_type,
          })}
        </Text>
      ),
      columnWidth: "1fr",
    },
    {
      value: "actions",
      label: "",
      renderCell: (data) => {
        if (data.source_url) {
          return (
            <AlignRight>
              <MoreButton
                editLabel={tPlaceholder("Edit at source")}
                onEdit={() => window.open(data.source_url, "_blank")}
              />
            </AlignRight>
          )
        }

        return (
          <AlignRight>
            <MoreButton
              onEdit={() =>
                navigate(
                  Routes.AiAssistantKnowledgeContent.location({
                    contextId: data.id,
                  }).pathname
                )
              }
              onDelete={() => deleteContext(data)}
            />
          </AlignRight>
        )
      },
      columnWidth: "auto",
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: enhancedPropertyContexts,
  })

  const isLoading =
    fetchPropertyContexts.isLoading ||
    fetchHomes.isLoading ||
    fetchHomegroups.isLoading

  if (isLoading) {
    return (
      <div>
        <Skeleton width="40ch" />
        <Skeleton width="30ch" />
        <Skeleton width="30ch" />
      </div>
    )
  }

  if (
    fetchPropertyContexts.isError ||
    fetchHomes.isError ||
    fetchHomegroups.isError
  ) {
    return (
      <Banner type="error" fullWidth>
        {t(langKeys.failed_contact_support)}
      </Banner>
    )
  }

  return (
    <div>
      <Header>
        <SearchFilter
          initialValue={search}
          onChange={setSearch}
          placeholder={tPlaceholder("Search for content")}
        />

        <Button onClick={() => setShowCreateContextDialog(true)} size="small">
          {tPlaceholder("Add content")}
        </Button>
      </Header>

      <TableWrapper>
        <GridTable
          header={headerElements}
          rows={rows}
          mobileRows={rows}
          minWidth={0}
          templateColumns={templateColumns}
          horizontalScroll
          disableRowHover
        />
      </TableWrapper>
    </div>
  )
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.L};
`

const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
`

const TableWrapper = styled.div`
  white-space: nowrap;
`
